import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useLocation, useNavigate} from 'react-router';

import {DEFAULT_CARD} from '@/app/main/onboarding/constants/OnboardingConstants';
import {useLoading} from '@/app/zustand/useLoading';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {useCountryCode} from '@/hooks';
import logError from '@/utils/errors/logError';

import {IKid} from '../../model/AddKidMode';
import {getKidData} from '../../services/handleKidInformation';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  kidData: Partial<IKid>;
  selectedTheme: string;
  showFamilyCodeBanner?: boolean;
  isCountryCodeVisible: boolean;
  handleEdit: () => void;
  handleCreateKid: () => void;
  handleKidLinked?: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  const kidData = getKidData();
  const {profile, refetchProfile} = useUsersRepository();
  const navigate = useNavigate();
  const {setSkipLoading} = useLoading();
  const {isCountryCodeVisible} = useCountryCode();

  const {
    trackSelectAddKidEditInfo,
    trackViewAddKidConfirmInfo,
    trackSelectAddKidConfirmEditInfo,
  } = useTrackers();
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(true);

  const selectedTheme =
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED || DEFAULT_CARD;

  useEffect(() => {
    trackViewAddKidConfirmInfo(
      Boolean(!kidData?.phone),
      selectedTheme,
      profile?.id || '',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = useCallback(() => {
    trackSelectAddKidEditInfo(
      Boolean(!kidData?.phone),
      selectedTheme,
      profile?.id || '',
    );
    navigate('/onboarding/add-kid/personal-info');
  }, [
    kidData?.phone,
    navigate,
    profile?.id,
    selectedTheme,
    trackSelectAddKidEditInfo,
  ]);

  const handleCreateKid = useCallback(() => {
    setSkipLoading(true);
    trackSelectAddKidConfirmEditInfo(
      Boolean(!kidData?.phone),
      selectedTheme,
      profile?.id || '',
    );
    navigate('/onboarding/add-kid/creating');
  }, [
    kidData?.phone,
    navigate,
    profile?.id,
    selectedTheme,
    setSkipLoading,
    trackSelectAddKidConfirmEditInfo,
  ]);

  const tryRefreshProfile = useCallback(async () => {
    try {
      await refetchProfile();
    } catch (error) {
      logError(error, {
        fileName: 'AddKid SummaryInfo ViewModelContext',
        functionName: 'tryRefreshProfile',
      });
    }
  }, [refetchProfile]);

  const handleKidLinked = useCallback(() => {
    tryRefreshProfile();
    setShowBanner(false);
  }, [tryRefreshProfile]);

  const showFamilyCodeBanner = useMemo(() => {
    return location.state?.showFamilyCodeBanner && showBanner;
  }, [location.state?.showFamilyCodeBanner, showBanner]);

  return (
    <ViewModelContext.Provider
      value={{
        kidData,
        selectedTheme,
        isCountryCodeVisible,
        handleEdit,
        handleCreateKid,
        showFamilyCodeBanner,
        handleKidLinked,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('Summary Info ViewModelContext is undefined');
  }
  return context;
}
